import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Store } from 'components/common/types/Store.types';
import { DealType } from 'components/common/types/Deal.types';
import { getIsReseller } from 'store/publisher/selectors';
import { getIsDV360AndPGAndDspSyncEnabled } from 'store/dealManagement/selectors';
import { getCurrentLineEndDate, getCurrentLineStartDate } from 'store/dealManagement/selectors/getCurrentLineSchedule';
import { getAdvertiser, getBrand, getProductCategory } from 'store/dealManagement/selectors/getCommonDealDetails';
import { getCurrentLineEnvironments } from 'store/dealManagement/selectors/getCurrentLineEnvironments';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { isDspSeatIdRequired } from 'utils/isDspSeatIdRequired';
import { MandatoryFieldsCheckResult } from './useCheckDirectSalesMandatoryFields';

export const useCheckProgrammaticMandatoryFields = (): MandatoryFieldsCheckResult & {
  areCampaignDrawerFieldsFilled: boolean;
} => {
  const isReseller = useSelector(getIsReseller);
  const { dsp, dspSeatId, syncWithDsp } = useSelector((state: Store) => state.dealManagement.programmatic);
  const dealName = useSelector((state: Store) => state.dealManagement.commonDeal.dealName);
  const advertiser = useSelector(getAdvertiser);
  const brand = useSelector(getBrand);
  const productCategory = useSelector(getProductCategory);
  const cpm = useSelector((state: Store) => state.dealManagement.commonDeal.cpm);
  const lineCpm = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.cpm);
  const isLineCpmCampaignLevel = useSelector((state: Store) => state.dealManagement.isCpmCampaignLevel);
  const dealType = useSelector(getDealType);
  const startDate = useSelector(getCurrentLineStartDate);
  const endDate = useSelector(getCurrentLineEndDate);
  const environments = useSelector(getCurrentLineEnvironments, shallowEqual);
  const impressions = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.impressions);
  const budget = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.budget);
  const mediaOwnerMeta = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.mediaOwnerMeta);
  const creativeType = useSelector((state: Store) => state.dealManagement.commonDeal.currentLine.creativeType);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const isDV360AndPGAndDspSyncEnabled = useSelector(getIsDV360AndPGAndDspSyncEnabled);
  const isDealTypeGuaranteed = dealType === DealType.GUARANTEED;

  const areDealInfoMandatoryFieldsFilled = useMemo(() => {
    return Object.values({
      startDate,
      endDate,
      dealName,
      dealType,
      dsp,
      ...(!isReseller && isDealTypeGuaranteed ? { advertiser } : {}),
      ...(!isReseller && isDealTypeGuaranteed ? { brand } : {}),
      ...(!isReseller && isDealTypeGuaranteed ? { productCategory } : {}),
      ...(!isReseller ? { haveEnvironments: environments.length > 0 } : {}),
      ...(isReseller ? { isMediaOwnerSelected: mediaOwnerMeta && mediaOwnerMeta.length > 0 } : {}),
      ...(!isLineCpmCampaignLevel ? { cpm: lineCpm } : {}),
      ...(isLineCpmCampaignLevel ? { cpm } : {}),
      ...(isDV360AndPGAndDspSyncEnabled ? { creativeType } : {}),
    }).every(Boolean);
  }, [
    dealName,
    dealType,
    startDate,
    endDate,
    advertiser,
    brand,
    productCategory,
    dsp,
    environments,
    cpm,
    isDV360AndPGAndDspSyncEnabled,
    creativeType,
    mediaOwnerMeta,
    lineCpm,
    isLineCpmCampaignLevel,
    isDealTypeGuaranteed,
  ]);

  const areCampaignDrawerFieldsFilled = useMemo(() => {
    const isRequiredDspSeatId = isDspSeatIdRequired(bookingStatusCode);

    return Object.values({
      dealName,
      dealType,
      dsp,
      ...(syncWithDsp && isRequiredDspSeatId ? { dspSeatId } : {}),
      ...(!isReseller && isDealTypeGuaranteed ? { advertiser } : {}),
      ...(!isReseller && isDealTypeGuaranteed ? { brand } : {}),
      ...(!isReseller && isDealTypeGuaranteed ? { productCategory } : {}),
      ...(isReseller ? { isMediaOwnerSelected: mediaOwnerMeta && mediaOwnerMeta.length > 0 } : {}),
      ...(isLineCpmCampaignLevel ? { cpm } : {}),
      ...(isDV360AndPGAndDspSyncEnabled ? { creativeType } : {}),
    }).every(Boolean);
  }, [
    advertiser,
    brand,
    productCategory,
    mediaOwnerMeta,
    dealName,
    dealType,
    dsp,
    creativeType,
    isDV360AndPGAndDspSyncEnabled,
    isLineCpmCampaignLevel,
    isReseller,
    cpm,
    isDealTypeGuaranteed,
    bookingStatusCode,
    syncWithDsp,
    dspSeatId,
  ]);

  const areAllocationMandatoryFieldsFilled = useMemo(() => {
    return Object.values({
      impressionsOrBudgetForGuaranteed: dealType !== DealType.GUARANTEED || Boolean(impressions || budget),
    }).every(Boolean);
  }, [dealType, impressions, budget]);

  const areAllMandatoryFieldsFilled = useMemo(() => {
    return Object.values({
      areDealInfoMandatoryFieldsFilled,
      areAllocationMandatoryFieldsFilled,
    }).every(Boolean);
  }, [areDealInfoMandatoryFieldsFilled, areAllocationMandatoryFieldsFilled]);

  return {
    areDealInfoMandatoryFieldsFilled,
    isAllocationTabEnabled: areDealInfoMandatoryFieldsFilled,
    isDataFiltersTabEnabled: areDealInfoMandatoryFieldsFilled,
    areAllocationMandatoryFieldsFilled,
    areAllMandatoryFieldsFilled,
    areCampaignDrawerFieldsFilled,
  };
};
